/*
* Copyright (c) 2014-2023 Inovalon, Inc. All Rights Reserved.
* This computer program is CONFIDENTIAL and a TRADE SECRET of Inovalon, Inc.
* The receipt or possession of this program does not convey any rights to use, reproduce or disclose
* its contents in whole or in part, without the specific written consent of Inovalon, Inc.
* Any use, reproduction or disclosure of this program without the express written consent of Inovalon, Inc.
* is a violation of the copyright laws and may subject you to criminal prosecution.
*/
import 'reflect-metadata'
import { injectable } from 'inversify'
import { IAccountFacilityApiClient } from './contracts'
import { IAccountFacilityGrid, IAccountFacilityGridCriteria, IAccountFacilityGridResults, IAccountFacilityDetail, IUserFacilityGridCriteria, IUserFacilityGridResults, IUserFacilityDetailRow } from '../AccountFacilityStateManager/contracts'
import { INpi } from '../RequestHistoryStateManager/contracts'

/**
 * This implementation of IAccountFacilityApiClient simulates 10K items as a database, adds some artificial delay to simulate a server
 *
 * @export
 * @class MockTransactionHistoryApiClient
 * @implements {IAccountFacilityApiClient}
 */
@injectable()
export class MockAccountFacilityApiClient implements IAccountFacilityApiClient {
	public facilities: IAccountFacilityGrid[] = []
	public users: IUserFacilityDetailRow[] = []
	public newFacility: IAccountFacilityGrid | undefined
	public newUsers: IUserFacilityDetailRow[] = []

	constructor() {
		for (let x = 1; x <= 20; x++) {
			this.users.push({
				id: x,
				users: { id: x, firstName: 'Betty' + x.toString(), lastName: 'Biller' + x.toString() },
				userSelection: { id: x, isSelected: false },
				isCorporateUser: false,
			})
			this.facilities.push({
				id: x,
				customerEntityId: 0,
				customerEntityFacilityStatusId: 1,
				practiceLocationStateCodeId: 1,
				facilityName: `Facility ${Math.round((Math.random() * 10) % 10)}`,
				active: true,
				deleted: false,
				npi: `${x}`,
				primaryPin: `${x}`,
				federalTaxId: `${x}`,
				facilityUserDetailRow: this.users,
				customerEntityFacilityUsers: [],
			})
		}
		for (let x = 1; x <= 20; x++) {
			this.newUsers.push({
				id: x,
				users: { id: x, firstName: 'Betty' + x.toString(), lastName: 'Biller' + x.toString() },
				userSelection: { id: x, isSelected: false },
				isCorporateUser: false,
			})
		}
		this.newFacility = {
			id: 0,
			customerEntityId: 0,
			customerEntityFacilityStatusId: 1,
			practiceLocationStateCodeId: 0,
			facilityName: '',
			active: true,
			deleted: false,
			npi: '',
			primaryPin: '',
			federalTaxId: '',
			facilityUserDetailRow: this.newUsers,
			customerEntityFacilityUsers: [],
		}
	}

	list(criteria: IAccountFacilityGridCriteria): Promise<IAccountFacilityGridResults> {
		const result: IAccountFacilityGridResults = {
			items: this.facilities.slice(),
			total: 0,
		}

		if (criteria.searchTerm) {
			result.items = result.items.filter(x => x.facilityName.toLowerCase().includes(criteria.searchTerm.toLowerCase()))
		}

		// sort
		const sortFunction = (x: IAccountFacilityGrid, y: IAccountFacilityGrid): number => {
			const getter = (i: IAccountFacilityDetail): any => {
				if (criteria.sortField === 'facilityName') {
					return i.facilityName
				} else if (criteria.sortField === 'id') {
					return i.id
				} else {
					throw new Error(`Unsupported sort column: [${criteria.sortField}]`)
				}
			}
			const xVal = getter(x)
			const yVal = getter(y)
			if (xVal === yVal) {
				return 0
			} else if (xVal < yVal) {
				return criteria.sortOrder === 'asc' ? -1 : 1
			} else {
				return criteria.sortOrder === 'asc' ? 1 : -1
			}
		}

		result.items.sort(sortFunction)
		result.total = result.items.length
		// paging
		result.items = result.items.slice((criteria.page - 1) * criteria.sizePerPage, (criteria.page - 1) * criteria.sizePerPage + criteria.sizePerPage)
		return new Promise<IAccountFacilityGridResults>(resolve => {
			setTimeout(() => resolve(result), 100)
		})
	}

	listUsers(id: number, criteria: IUserFacilityGridCriteria): Promise<IUserFacilityGridResults> {
		const result: IUserFacilityGridResults = {
			items: this.users.slice(),
			total: 0,
		}

		// sort
		const sortFunction = (x: IUserFacilityDetailRow, y: IUserFacilityDetailRow): number => {
			const getter = (i: IUserFacilityDetailRow): any => {
				if (criteria.sortField === 'users') {
					return i.users
				} else {
					throw new Error(`Unsupported sort column: [${criteria.sortField}]`)
				}
			}
			const xVal = getter(x)
			const yVal = getter(y)
			if (xVal === yVal) {
				return 0
			} else if (xVal < yVal) {
				return criteria.sortOrder === 'asc' ? -1 : 1
			} else {
				return criteria.sortOrder === 'asc' ? 1 : -1
			}
		}

		result.items.sort(sortFunction)
		result.total = result.items.length
		// paging
		result.items = result.items.slice((criteria.page - 1) * criteria.sizePerPage, (criteria.page - 1) * criteria.sizePerPage + criteria.sizePerPage)
		return new Promise<IUserFacilityGridResults>(resolve => {
			setTimeout(() => resolve(result), 100)
		})
	}

	getAccountFacility(id: number): Promise<IAccountFacilityDetail> {
		if (id !== 0) {
			return new Promise<IAccountFacilityDetail>(resolve => {
				resolve(this.facilities.filter(x => x.id === id)[0])
			})
		} else {
			return new Promise<IAccountFacilityDetail>(resolve => {
				resolve(this.newFacility)
			})
		}
	}

	saveAccountFacility(item: IAccountFacilityDetail): Promise<void> {
		return new Promise<void>(resolve => {
			if (Math.round(Math.random())) {
				throw new Error('I only work half the time.')
			}
			item.id = Math.max(...this.facilities.map(x => x.id)) + 1
			this.facilities.push(item)
			setTimeout(() => resolve(), 100)
		})
	}

	updateAccountFacility(item: IAccountFacilityDetail): Promise<void> {
		return new Promise<void>(resolve => {
			const match = this.facilities.filter(x => x.id === item.id)[0]
			match.facilityName = item.facilityName
			match.facilityUserDetailRow = item.facilityUserDetailRow
			match.active = item.active
			match.npi = item.npi
			match.primaryPin = item.primaryPin
			match.federalTaxId = item.federalTaxId
			setTimeout(() => resolve(), 100)
		})
	}

	getAllNpis(): Promise<INpi[]> {
		return new Promise<INpi[]>(resolve =>
			resolve([
				{ id: 1, name: 'Npi1', isSelected: false },
				{ id: 2, name: 'Npi2', isSelected: false },
				{ id: 3, name: 'Npi3', isSelected: false },
				{ id: 4, name: 'Npi4', isSelected: false },
				{ id: 5, name: 'Npi5', isSelected: false },
				{ id: 6, name: 'Npi6', isSelected: false },
				{ id: 7, name: 'Npi7', isSelected: false },
				{ id: 8, name: 'Npi8', isSelected: false },
				{ id: 9, name: 'Npi9', isSelected: false },
				{ id: 10, name: 'Npi10', isSelected: false },
				{ id: 11, name: 'Npi12', isSelected: false },
				{ id: 12, name: 'Npi13', isSelected: false },
				{ id: 13, name: 'Npi14', isSelected: false },
				{ id: 14, name: 'Npi15', isSelected: false },
				{ id: 15, name: 'Npi16', isSelected: false },
				{ id: 16, name: 'Npi17', isSelected: false },
				{ id: 17, name: 'Npi18', isSelected: false },
				{ id: 18, name: 'Npi19', isSelected: false },
				{ id: 19, name: 'Npi20', isSelected: false },
				{ id: 20, name: 'Npi22', isSelected: false },
				{ id: 21, name: 'Npi23', isSelected: false },
				{ id: 22, name: 'Npi24', isSelected: false },
				{ id: 23, name: 'Npi25', isSelected: false },
				{ id: 24, name: 'Npi26', isSelected: false },
				{ id: 25, name: 'Npi27', isSelected: false },
				{ id: 26, name: 'Npi28', isSelected: false },
				{ id: 27, name: 'Npi29', isSelected: false },
			]),
		)
	}
}
